import { useState } from 'react';
import { Link } from 'react-router-dom';
import { Seo, Mark, Container , VideoSlot } from '../components/marketing/Primitives';
import { useVideo } from '../cms/ContentProvider';
import {
  liveUrl,
  tuningSpecimen,
  examFamilies,
  howItWorks,
  outputs,
  creditPacks,
  proPlans,
  pricingNotes,
  honesty,
  faq,
} from '../data/expressData';

/* The interactive specimen: one uploaded excerpt, three exam families, three
   output types. Switching the family restyles the question and the guide;
   flashcards stay exam-agnostic, exactly as the product behaves. */
function TuningSpecimen() {
  const [familyId, setFamilyId] = useState('usmle2');
  const [tab, setTab] = useState('question');
  const [answered, setAnswered] = useState(null);

  const family = tuningSpecimen.families.find((f) => f.id === familyId);
  const q = family.question;

  function switchFamily(id) {
    setFamilyId(id);
    setAnswered(null);
  }

  return (
    <div className="specimen-card !rotate-0">
      {/* The source */}
      <p className="text-[11px] font-semibold tracking-wide text-slate">{tuningSpecimen.sourceLabel}</p>
      <p className="mt-2 rounded border border-hairline bg-ink-deep/60 p-3 text-[12px] leading-relaxed text-slate">
        {tuningSpecimen.sourceText}
      </p>

      {/* Tuning controls */}
      <div className="mt-4 flex flex-wrap items-center gap-2">
        <p className="text-[11px] text-faint">Exam family:</p>
        {tuningSpecimen.families.map((f) => (
          <button
            key={f.id}
            type="button"
            onClick={() => switchFamily(f.id)}
            aria-pressed={f.id === familyId}
            className={`rounded border px-2.5 py-1 text-[11px] font-semibold transition-colors ${
              f.id === familyId ? 'border-seal bg-seal/10 text-seal' : 'border-hairline text-slate hover:text-bright'
            }`}
          >
            {f.name}
          </button>
        ))}
      </div>
      <div className="mt-2 flex flex-wrap items-center gap-2">
        <p className="text-[11px] text-faint">Output:</p>
        {[
          { id: 'question', label: 'Question' },
          { id: 'guide', label: 'Study guide' },
          { id: 'card', label: 'Flashcard' },
        ].map((t) => (
          <button
            key={t.id}
            type="button"
            onClick={() => setTab(t.id)}
            aria-pressed={tab === t.id}
            className={`rounded border px-2.5 py-1 text-[11px] font-semibold transition-colors ${
              tab === t.id ? 'border-seal bg-seal/10 text-seal' : 'border-hairline text-slate hover:text-bright'
            }`}
          >
            {t.label}
          </button>
        ))}
      </div>

      {/* Output */}
      <div className="mt-4 border-t border-hairline pt-4">
        {tab === 'question' && (
          <div>
            <p className="text-[13px] leading-relaxed text-bright">{q.stem}</p>
            <div className="mt-3 space-y-2">
              {q.options.map((opt) => {
                const revealed = answered !== null;
                const isKey = opt.key === q.correctKey;
                const isPick = opt.key === answered;
                let cls = 'border-hairline text-slate hover:border-seal/50 hover:text-bright';
                if (revealed && isKey) cls = 'border-emerald-400/70 bg-emerald-400/10 text-bright';
                else if (revealed && isPick) cls = 'border-rose-400/70 bg-rose-400/10 text-bright';
                else if (revealed) cls = 'border-hairline text-faint';
                return (
                  <button
                    key={opt.key}
                    type="button"
                    disabled={answered !== null}
                    onClick={() => setAnswered(opt.key)}
                    className={`w-full rounded border px-3 py-2 text-left text-[12px] leading-snug transition-colors ${cls}`}
                  >
                    <span className="mr-2 font-semibold text-bright">{opt.key}.</span>
                    {opt.text}
                  </button>
                );
              })}
            </div>
            {answered !== null && (
              <div className="mt-3">
                <p className={`text-[12px] font-semibold ${answered === q.correctKey ? 'text-seal' : 'text-rose-300'}`}>
                  {answered === q.correctKey ? 'Correct' : `Answer: ${q.correctKey}`}
                </p>
                <p className="mt-1.5 text-[12px] leading-relaxed text-slate">{q.explanation}</p>
              </div>
            )}
          </div>
        )}

        {tab === 'guide' && (
          <ul className="space-y-2">
            {family.guide.map((line) => (
              <li key={line} className="flex gap-2.5 text-[12px] leading-relaxed text-slate">
                <span aria-hidden="true" className="mt-1.5 inline-block h-1.5 w-1.5 shrink-0 rounded-[1px] bg-seal" />
                {line}
              </li>
            ))}
          </ul>
        )}

        {tab === 'card' && (
          <div>
            <FlipCard front={tuningSpecimen.flashcard.front} back={tuningSpecimen.flashcard.back} />
            <p className="mt-2 text-[10px] leading-snug text-faint">{tuningSpecimen.flashcard.note}</p>
          </div>
        )}
      </div>

      <p className="mt-4 border-t border-hairline pt-3 text-[10px] leading-snug text-faint">
        Illustrative sample output. Express generates from your uploads; it carries no provenance claim, and the output
        is only as good as your material.
      </p>
    </div>
  );
}

function FlipCard({ front, back }) {
  const [flipped, setFlipped] = useState(false);
  return (
    <button
      type="button"
      onClick={() => setFlipped((v) => !v)}
      className="w-full rounded border border-hairline bg-ink-deep/60 px-4 py-6 text-center transition-colors hover:border-seal/50"
      aria-pressed={flipped}
    >
      <p className="text-[10px] font-semibold text-faint">{flipped ? 'Back (click to flip)' : 'Front (click to flip)'}</p>
      <p className="mt-2 text-[13px] font-medium text-bright">{flipped ? back : front}</p>
    </button>
  );
}

export default function ExpressPage() {
  const [openFaq, setOpenFaq] = useState(null);
  const demoVideo = useVideo('express.upload', null);

  return (
    <>
      <Seo
        title="EdAI Express"
        description="The tools without the content: upload your own material and get exam-tuned practice questions, a study guide, and Anki-ready flashcards in about a minute. Live at edai-express.com."
      />

      {/* Hero: claim left, tuning specimen right. */}
      <section className="overflow-hidden bg-ink text-paper-dark-text">
        <Container className="grid items-start gap-14 py-20 md:py-28 lg:grid-cols-[1fr_1.1fr] lg:gap-12">
          <div className="lg:pt-6">
            <Mark dark>EdAI Express · For any learner, any domain</Mark>
            <h1 className="hero-rise mt-4 font-serif text-4xl leading-[1.06] md:text-5xl">
              The tools without the content.
            </h1>
            <p className="hero-rise-2 mt-6 max-w-measure text-lg leading-relaxed text-slate-dark-text">
              Your own material, transformed into exam-tuned study tools in about a minute: practice questions with
              explanations, a structured study guide, and Anki-ready flashcards.
            </p>
            <p className="hero-rise-2 mt-4 max-w-measure text-sm leading-relaxed text-faint">
              On the right: one paragraph from an uploaded lecture, retuned per exam family. Switch families and watch
              the question change register, and the drug change country.
            </p>
            <div className="hero-rise-3 mt-9 flex flex-wrap gap-4">
              <a href={liveUrl} target="_blank" rel="noreferrer" className="btn-primary-on-dark">
                Try it with your own material
              </a>
              <a href="#pricing" className="btn-secondary-on-dark">See pricing</a>
            </div>
          </div>
          <div className="hero-rise-3">
            <TuningSpecimen />
          </div>
        </Container>
      </section>

      {/* Scope note: the brand-required honesty, early and plain. */}
      <section className="border-b border-hairline bg-ink-deep/60 py-6">
        <Container>
          <p className="max-w-measure-wide text-sm leading-relaxed text-slate">
            <span className="font-semibold text-bright">A note on scope: </span>
            Express works on whatever you bring it, which means the output is only as good as your material. Express
            content is generated from your uploads, not from board-designated sources, and does not carry the
            provenance guarantees of EdAI’s authority-derived products.
          </p>
        </Container>
      </section>

      {/* How it works */}
      <section className="py-20 md:py-28">
        <Container>
          <Mark>How it works</Mark>
          <h2 className="mt-3 max-w-[24ch] font-serif text-3xl leading-tight md:text-4xl">
            A chapter in. A study kit out. About a minute.
          </h2>
          <div className="mt-12 grid gap-10 md:grid-cols-3 md:gap-0 md:divide-x md:divide-hairline">
            {howItWorks.map((step, i) => (
              <article key={step.name} className="md:px-8 md:first:pl-0 md:last:pr-0">
                <h3 className="flex items-center gap-3 font-serif text-xl text-bright">
                  <span
                    aria-hidden="true"
                    className="flex h-8 w-8 items-center justify-center rounded-full border border-seal/60 text-sm text-seal"
                  >
                    {i + 1}
                  </span>
                  {step.name}
                </h3>
                <p className="mt-4 leading-relaxed text-slate">{step.text}</p>
              </article>
            ))}
          </div>
          <p className="mt-10 max-w-measure-wide text-sm leading-relaxed text-faint">
            Exam families today: {examFamilies.join(', ')}. The engine is domain-agnostic by design: the same
            architecture serves law, engineering, and any professional certification domain.
          </p>
        </Container>
      </section>

      {/* Outputs */}
      <section className="border-y border-hairline bg-ink-deep/40 py-20 md:py-28">
        <Container>
          <Mark>What you get back</Mark>
          <h2 className="mt-3 font-serif text-3xl leading-tight md:text-4xl">Three outputs. Every export free.</h2>
          <div className="mt-12 grid gap-px overflow-hidden rounded-media border border-hairline bg-hairline md:grid-cols-3">
            {outputs.map((o) => (
              <div key={o.name} className="bg-surface p-7">
                <h3 className="font-serif text-xl text-bright">{o.name}</h3>
                <p className="mt-3 text-sm leading-relaxed text-slate">{o.text}</p>
              </div>
            ))}
          </div>
        </Container>
      </section>

      {/* Pricing */}
      <section id="pricing" className="py-20 md:py-28">
        <Container>
          <Mark>Pricing</Mark>
          <h2 className="mt-3 max-w-[22ch] font-serif text-3xl leading-tight md:text-4xl">
            Pay as you go, or go Pro.
          </h2>
          <div className="mt-12 grid gap-12 lg:grid-cols-[1.2fr_1fr] lg:gap-16">
            <div>
              <h3 className="text-sm font-semibold text-bright">Credit packs (one-time, never expire)</h3>
              <div className="mt-4 grid gap-px overflow-hidden rounded-media border border-hairline bg-hairline sm:grid-cols-2">
                {creditPacks.map((p) => (
                  <div key={p.name} className="flex items-baseline justify-between gap-4 bg-surface px-6 py-5">
                    <div>
                      <p className="font-serif text-lg text-bright">{p.name}</p>
                      <p className="text-sm text-slate">{p.credits}</p>
                    </div>
                    <p className="text-lg font-semibold text-seal">{p.price}</p>
                  </div>
                ))}
              </div>
            </div>
            <div>
              <h3 className="text-sm font-semibold text-bright">Pro subscription</h3>
              <div className="mt-4 space-y-px overflow-hidden rounded-media border border-hairline bg-hairline">
                {proPlans.map((p) => (
                  <div key={p.name} className="bg-surface px-6 py-5">
                    <p className="font-serif text-lg text-bright">{p.name}</p>
                    <p className="mt-1 text-sm text-slate">{p.price}</p>
                  </div>
                ))}
              </div>
            </div>
          </div>
          <ul className="mt-8 space-y-2">
            {pricingNotes.map((n) => (
              <li key={n} className="flex gap-2.5 text-sm leading-relaxed text-slate">
                <span aria-hidden="true" className="mt-2 inline-block h-1.5 w-1.5 shrink-0 rounded-[1px] bg-seal" />
                {n}
              </li>
            ))}
          </ul>
        </Container>
      </section>

      {/* Honesty */}
      <section className="border-y border-hairline bg-ink py-20 text-paper-dark-text md:py-28">
        <Container>
          <Mark dark>Honest by design</Mark>
          <h2 className="mt-3 max-w-[22ch] font-serif text-3xl leading-tight md:text-4xl">
            Small print, printed large.
          </h2>
          <div className="mt-12 grid gap-x-14 gap-y-10 md:grid-cols-2">
            {honesty.map((h) => (
              <div key={h.title} className="border-t border-hairline-dark pt-5">
                <h3 className="font-serif text-lg text-bright">{h.title}</h3>
                <p className="mt-2 leading-relaxed text-slate-dark-text">{h.text}</p>
              </div>
            ))}
          </div>
        </Container>
      </section>

      {/* Bridge to Prep */}
      <section className="py-16 md:py-20">
        <Container>
          <div className="flex flex-col items-start justify-between gap-6 rounded-media border border-hairline bg-surface p-8 md:flex-row md:items-center md:p-10">
            <div>
              <h2 className="font-serif text-2xl text-bright">Want the certified corpus underneath?</h2>
              <p className="mt-2 max-w-measure-wide leading-relaxed text-slate">
                Express transforms your material. EdAI Prep prepares you from board-designated, physician-certified
                sources with provenance on every item, for USMLE, MCCQE, UKMLA, AMC, and PRES. Different jobs; pick by
                what you need, or use both.
              </p>
            </div>
            <Link to="/products/prep" className="btn-secondary shrink-0">
              Explore EdAI Prep
            </Link>
          </div>
        </Container>
      </section>


      {/* Demo film, published from the content console at /admin */}
      <section className="border-y border-hairline bg-ink py-20 text-paper-dark-text md:py-24">
        <Container>
          <Mark dark>See it</Mark>
          <h2 className="mt-3 font-serif text-3xl leading-tight md:text-4xl">Watch an upload, start to finish</h2>
          <p className="mt-4 max-w-measure leading-relaxed text-slate-dark-text">A real upload: a lecture PDF in, and the questions, study guide, and Anki export it produces about a minute later.</p>
          <div className="mt-10 lg:max-w-measure-wide">
            <VideoSlot video={demoVideo} title="Watch an upload, start to finish" text="A real upload: a lecture PDF in, and the questions, study guide, and Anki export it produces about a minute later." />
          </div>
        </Container>
      </section>
      {/* FAQ */}
      <section className="border-t border-hairline py-20 md:py-28">
        <Container>
          <Mark>Questions learners ask</Mark>
          <h2 className="mt-3 font-serif text-3xl leading-tight md:text-4xl">Asked and answered.</h2>
          <div className="mt-10 max-w-measure-wide divide-y divide-hairline border-y border-hairline">
            {faq.map((item, i) => (
              <div key={item.q}>
                <button
                  type="button"
                  onClick={() => setOpenFaq(openFaq === i ? null : i)}
                  aria-expanded={openFaq === i}
                  className="flex w-full items-baseline justify-between gap-6 py-5 text-left"
                >
                  <span className="font-semibold text-bright">{item.q}</span>
                  <span aria-hidden="true" className="text-seal">{openFaq === i ? '\u2212' : '+'}</span>
                </button>
                {openFaq === i && <p className="pb-6 leading-relaxed text-slate">{item.a}</p>}
              </div>
            ))}
          </div>
        </Container>
      </section>

      {/* Closing CTA */}
      <section className="border-t border-hairline bg-ink-deep/40 py-20">
        <Container className="text-center">
          <h2 className="mx-auto max-w-[26ch] font-serif text-3xl leading-tight md:text-4xl">
            The material is already on your laptop. Bring it.
          </h2>
          <div className="mt-8 flex flex-wrap justify-center gap-4">
            <a href={liveUrl} target="_blank" rel="noreferrer" className="btn-primary">
              Try it with your own material
            </a>
          </div>
        </Container>
      </section>
    </>
  );
}
